import { type FC, type ReactNode, useEffect } from "react";
import { AnimatePresence, motion } from "motion/react";
import { useTranslation } from "react-i18next";
import { Container, Flex, Heading, Icon, Stack, Text } from "@huub-web/ui";
import { TemplatesParser, type TemplateCollection } from "@huub-web/templating";
import { useTemplatesAPI } from "@huub-web/backend";
import { mergeClasses } from "@huub-web/utils";
import { Illustration, Section } from "@huub-web/shared";

import { useAppController } from "../../hooks/useAppController";
import { usePageReady } from "../../hooks/usePageReady";
import { templatesScreenChildMotionProps } from "./TemplatesScreen.motion";

import {
  contentStyle,
  containerStyle,
  childContainerStyle,
} from "./TemplatesScreen.css";

type TemplatesScreenProps = {
  /**
   * The leading component to display.
   */
  leading?: ReactNode;
  /**
   * The endpoint to fetch templates from.
   */
  endpoint?: string;
};

/**
 * A screen that displays templates.
 */
export const TemplatesScreen: FC<TemplatesScreenProps> = ({
  leading,
  endpoint,
}) => {
  const { t } = useTranslation();
  const {
    data: templatesCollection,
    error,
    loading,
    isFetching,
  } = useTemplatesAPI<TemplateCollection>(endpoint);

  const {
    activateCustomErrorState,
    deactivateCustomErrorState,
    activateCustomLoadingState,
    deactivateCustomLoadingState,
  } = useAppController();

  usePageReady({ loading: isFetching });

  useEffect(() => {
    if (error && !loading && error !== "Not Found") {
      activateCustomErrorState();
    }
    return () => {
      deactivateCustomErrorState();
    };
  }, [error, loading]);

  useEffect(() => {
    if (isFetching) {
      activateCustomLoadingState();
    } else {
      deactivateCustomLoadingState();
    }
    return () => {
      deactivateCustomLoadingState();
    };
  }, [isFetching]);

  return (
    <>
      {leading}

      <Stack
        data-stack="templates-screen"
        className={containerStyle}
        childClassName={childContainerStyle}
      >
        <AnimatePresence>
          {!endpoint && !error && (
            <motion.div {...templatesScreenChildMotionProps}>
              <Container>
                <Text color="error">
                  <Flex gap="s" align="center" justify="center">
                    <Icon icon="alertFilled" />
                    Endpoint not implemented
                  </Flex>
                </Text>
              </Container>
            </motion.div>
          )}
        </AnimatePresence>

        <AnimatePresence>
          {!loading && error && (
            <motion.div {...templatesScreenChildMotionProps}>
              <Section>
                <Container size="marketing">
                  <Flex direction="column" gap="l">
                    <Illustration
                      name={error === "Not Found" ? "notFound" : "shrug"}
                      alt={error === "Not Found" ? "404" : "Error"}
                      alignSelf="center"
                      size="m"
                    />
                    <Heading
                      variant="secondary"
                      title={error === "Not Found" ? t("404.title") : undefined}
                      description={
                        error === "Not Found"
                          ? t("404.body")
                          : t("general.error")
                      }
                    />
                  </Flex>
                </Container>
              </Section>
            </motion.div>
          )}
        </AnimatePresence>

        <AnimatePresence>
          {templatesCollection && endpoint && !error && (
            <motion.div
              {...templatesScreenChildMotionProps}
              className={mergeClasses(contentStyle, "templates-content")}
            >
              <TemplatesParser templates={templatesCollection} />
            </motion.div>
          )}
        </AnimatePresence>
      </Stack>
    </>
  );
};
